import JSZip from "jszip";

/**
 * Calculate SHA1 hash of a file using Web Crypto API
 */
export async function calculateSHA1(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-1", arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}

export interface ProcessedDirectory {
  zip: Blob;
  checksums: Record<string, string>;
  zipFileName: string;
}

/**
 * Process a directory selection: calculate checksums, create checksum file, and zip everything
 * @param files - The files to process
 * @param onProgress - Optional callback to report progress (0-100)
 */
export async function processDirectory(
  files: FileList,
  onProgress?: (progress: number) => void,
): Promise<ProcessedDirectory> {
  const zip = new JSZip();
  const checksums: Record<string, string> = {};

  // Get the root directory name from the first file's webkitRelativePath
  let rootDirectoryName = "";
  if (files.length > 0 && files[0].webkitRelativePath) {
    const firstPath = files[0].webkitRelativePath;
    const parts = firstPath.split("/");
    if (parts.length > 0 && parts[0]) {
      rootDirectoryName = parts[0];
    }
  }

  // If we couldn't determine root directory name, try to infer from file paths
  if (!rootDirectoryName && files.length > 0 && files[0].webkitRelativePath) {
    // Try to get a common prefix from all paths
    const paths = Array.from(files)
      .map((f) => f.webkitRelativePath)
      .filter((p) => p && p.length > 0);
    if (paths.length > 0) {
      const firstPathParts = paths[0].split("/");
      if (firstPathParts.length > 0) {
        rootDirectoryName = firstPathParts[0];
      }
    }
  }

  // If we still couldn't determine root directory name, use a timestamped default
  if (!rootDirectoryName) {
    rootDirectoryName = `directory_upload_${Date.now()}`;
  }

  // Clean the directory name (remove invalid characters for filenames)
  rootDirectoryName = rootDirectoryName.replace(/[<>:"/\\|?*]/g, "_");

  // Helper to sanitize relative paths and prevent path traversal
  function sanitizeRelativePath(path: string, fallbackName: string): string {
    // Remove any leading slashes
    let sanitized = path.replace(/^[/\\]+/, "");
    // Remove any '..' segments
    if (
      sanitized.includes("..") ||
      sanitized.startsWith("/") ||
      sanitized.startsWith("\\") ||
      sanitized.includes("\\")
    ) {
      // Fallback to just the filename if path is suspicious
      return fallbackName.replace(/[<>:"/\\|?*]/g, "_");
    }
    // Remove invalid filename characters from each segment
    sanitized = sanitized
      .split("/")
      .map((seg) => seg.replace(/[<>:"/\\|?*]/g, "_"))
      .join("/");
    return sanitized;
  }

  const fileArray = Array.from(files);
  const totalFiles = fileArray.length;

  // Report initial progress
  if (onProgress) {
    onProgress(0);
  }

  // Process each file: calculate checksum and add to zip
  // Process files sequentially to provide accurate progress updates
  for (let i = 0; i < fileArray.length; i++) {
    const file = fileArray[i];
    const checksum = await calculateSHA1(file);
    const relativePath = file.webkitRelativePath || file.name;
    const safeRelativePath = sanitizeRelativePath(relativePath, file.name);
    // Store checksum with the relative path as the key
    checksums[safeRelativePath] = checksum;

    // Read file content and add to zip maintaining directory structure
    const fileContent = await file.arrayBuffer();
    zip.file(relativePath, fileContent);

    // Update progress after each file is processed
    // Reserve 80% for file processing, 20% for zip generation
    const progress = Math.floor(((i + 1) / totalFiles) * 80);
    if (onProgress) {
      onProgress(progress);
    }
  }

  // Create checksum file with JSON format
  const checksumContent = JSON.stringify(checksums, null, 2);
  zip.file("checksums.json", checksumContent);

  // Report progress before zip generation
  if (onProgress) {
    onProgress(85);
  }

  // Generate zip blob
  const zipBlob = await zip.generateAsync({ type: "blob" }, (metadata) => {
    // Update progress during zip generation (85-100%)
    if (onProgress) {
      const zipProgress = 85 + Math.floor((metadata.percent / 100) * 15);
      onProgress(zipProgress);
    }
  });

  // Report completion
  if (onProgress) {
    onProgress(100);
  }

  // Create zip file name based on directory name
  const zipFileName = `${rootDirectoryName}.zip`;

  return {
    checksums,
    zip: zipBlob,
    zipFileName,
  };
}

/**
 * Check if the files are from a directory selection (have webkitRelativePath)
 */
export function isDirectoryUpload(files: FileList): boolean {
  if (files.length === 0) return false;
  return Array.from(files).every(
    (file) => file.webkitRelativePath && file.webkitRelativePath.length > 0,
  );
}
